import { useEffect, useState, useSyncExternalStore } from 'react'

export const DEFAULT_STEP_TIME = 1000

/**
 * Hook which returns the provided steps one at a time, to simulate loading.
 * ~1 second per step
 *
 * If the provided steps are ever change (according to shallow equal), then
 * the loading starts over
 *
 * If fuzzTiming is true (the default), the time per step is slightly randomized
 */
export function useSimulatedLoadingSteps(
  steps: readonly string[],
  {
    fuzzTiming,
    totalTime,
  }: {
    fuzzTiming?: boolean
    totalTime?: number
  },
): readonly string[] {
  const [simulator] = useState(() => new StepSimulator(steps, { fuzzTiming, totalTime }))
  useEffect(() => {
    simulator.setSteps(steps, totalTime)
  }, [simulator, steps, totalTime])

  return useSyncExternalStore(simulator.subscribe, () => simulator.getLoadedSteps())
}

class StepSimulator {
  private _steps: readonly string[]
  private _loadedSteps: readonly string[]
  private _timeout: number | undefined
  private _fuzzTiming: boolean
  private _totalTime: number
  private _onUpdate?: () => void

  constructor(
    steps: readonly string[],
    { fuzzTiming, totalTime }: { fuzzTiming?: boolean; totalTime?: number },
  ) {
    this._steps = steps
    this._loadedSteps = [steps[0]]
    this._fuzzTiming = fuzzTiming ?? true
    this._totalTime = totalTime ?? steps.length * DEFAULT_STEP_TIME
  }

  setSteps(steps: readonly string[], totalTime?: number) {
    this._totalTime = totalTime ?? steps.length * DEFAULT_STEP_TIME
    if (!this._stepsEqual(steps)) {
      this._steps = steps
      this._loadedSteps = [steps[0]]
      this._clearTimeout()
      this._triggerUpdate()
    }
  }

  subscribe = (onUpdate: () => void) => {
    this._onUpdate = onUpdate
    this._scheduleNextStepIfNeeded()
    return () => {
      this._clearTimeout()
    }
  }

  getLoadedSteps = (): readonly string[] => this._loadedSteps

  private _stepsEqual = (newSteps: readonly string[]) => {
    return (
      this._steps.length === newSteps.length &&
      this._steps.every((str, idx) => str === newSteps[idx])
    )
  }

  private _scheduleNextStepIfNeeded = (): void => {
    if (this._loadedSteps.length === this._steps.length || this._timeout !== undefined) {
      return
    }

    const stepTime = this._totalTime / this._steps.length

    const fuzzedTime = this._fuzzTiming
      ? stepTime + (Math.random() > 0.5 ? 1 : -1) * ((Math.random() * stepTime) / 3)
      : stepTime
    this._timeout = window.setTimeout(() => {
      this._timeout = undefined
      this._loadedSteps = this._steps.slice(0, this._loadedSteps.length + 1)
      this._triggerUpdate()
    }, fuzzedTime)
  }

  private _triggerUpdate = (): void => {
    if (this._onUpdate) {
      this._onUpdate?.()
      this._scheduleNextStepIfNeeded()
    }
  }

  private _clearTimeout = (): void => {
    window.clearTimeout(this._timeout)
    this._timeout = undefined
  }
}
